import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { GET_SUBSCRIPTIONS } from '../subscriptions/subscriptions.graphql';
import { SubscriptionModel } from '../interfaces';
import { GET_LICENSES } from '../licenses/overview/License.graphql';
import { GET_LICENSE } from '../licenses/details/LicenseInfo.graphql';
import { GET_USER, GET_USERS } from '../users/user.graphql';
import { CREATE_USAGE_LIMIT, GET_USAGE_LIMITS, UPDATE_USAGE_LIMIT } from './usagelimits.graphql';
import { BYGGFORSKSERIEN, VAATROMSNORMEN, productName, productOfSource } from './products';
import { UsageLimitRule } from './interfaces';
import { useDebouncedSearch } from '../components/useDebouncedSearch';

type Scope = 'subscription' | 'license' | 'user' | 'global';

interface PickedOption {
  id: number;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Set to edit an existing rule; omit to create a new one. */
  rule?: UsageLimitRule | null;
  /** Locks the dialog to a user-scoped rule for this user (support flow). */
  presetUserId?: number;
  /** Locks the product (support flow from a product section). */
  presetSourceId?: number;
  presetSubscriptionId?: number;
  onSaved?: () => void;
}

export function RuleDialog({ open, onClose, rule, presetUserId, presetSourceId, presetSubscriptionId, onSaved }: Props) {
  const isEdit = !!rule;

  const [sourceId, setSourceId] = useState<number>(presetSourceId ?? rule?.sourceId ?? BYGGFORSKSERIEN);
  const [scope, setScope] = useState<Scope>(
    rule?.userId || presetUserId ? 'user' : rule?.licenseId ? 'license' : rule && rule.subscriptionIds.length === 0 ? 'global' : 'subscription',
  );
  const [subscriptionIds, setSubscriptionIds] = useState<number[]>(rule?.subscriptionIds ?? (presetSubscriptionId ? [presetSubscriptionId] : []));
  const [pickedLicense, setPickedLicense] = useState<PickedOption | null>(null);
  const [pickedUser, setPickedUser] = useState<PickedOption | null>(null);
  const [licenseTerm, setLicenseTerm] = useState('');
  const [userTerm, setUserTerm] = useState('');
  const [maxDocuments, setMaxDocuments] = useState(rule?.maxDocuments?.toString() ?? '');
  const [documentWindowDays, setDocumentWindowDays] = useState(rule?.documentWindowDays?.toString() ?? '');
  const [maxAiTokens, setMaxAiTokens] = useState(rule?.maxAiTokens?.toString() ?? '');
  const [aiTokenWindowDays, setAiTokenWindowDays] = useState(rule?.aiTokenWindowDays?.toString() ?? '');
  const [active, setActive] = useState(rule?.active ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  // The rule stores BKS ids; the preset one is offered even when it is not for sale.
  const subscriptionsQuery = useQuery<{ subscriptions: SubscriptionModel[] }>(GET_SUBSCRIPTIONS);
  const subscriptions = (subscriptionsQuery.data?.subscriptions ?? [])
    .filter((s) => (s.isSellable || s.bksId === presetSubscriptionId) && s.product === productOfSource(sourceId))
    .sort((a, b) => a.bksId - b.bksId)
    .map((s) => ({ id: s.bksId, name: s.name }));

  const [searchLicenses, licenseSearch] = useLazyQuery<any, any>(GET_LICENSES, { fetchPolicy: 'no-cache' });
  const [searchUsers, userSearch] = useLazyQuery<any, any>(GET_USERS, { fetchPolicy: 'no-cache' });
  // Neither backend search matches on the entity id, so a purely numeric term additionally runs
  // the by-id lookup and the hit is merged in on top of the text-search results.
  const [getLicenseById, licenseByIdSearch] = useLazyQuery<any, any>(GET_LICENSE, { fetchPolicy: 'no-cache' });
  const [getUserById, userByIdSearch] = useLazyQuery<any, any>(GET_USER, { fetchPolicy: 'no-cache' });
  const isNumeric = (term: string) => /^\d+$/.test(term);

  // onlyInformed MUST be false: AllLicenses treats a missing value as true, which RESTRICTS the
  // result to the informed-licenses list. onlyActive keeps deleted licenses out of the picker.
  const licenseSearchControl = useDebouncedSearch((term) =>
    Promise.all([
      searchLicenses({ variables: { page: 0, pageSize: 20, searchTerm: term, onlyActive: true, onlyInformed: false } }),
      isNumeric(term) ? getLicenseById({ variables: { id: Number(term) } }) : Promise.resolve(),
    ]),
  );
  // GetUsers' `filter` is a DataGrid filter-model JSON; quickFilterValues searches name/email/login.
  const userSearchControl = useDebouncedSearch((term) =>
    Promise.all([
      searchUsers({
        variables: {
          page: 0,
          pageSize: 20,
          onlyActive: false,
          activeSingleDocument: false,
          nonActiveSingleDocument: false,
          nonActiveForNumberYears: 0,
          filter: JSON.stringify({ items: [], quickFilterValues: [term] }),
        },
      }),
      isNumeric(term) ? getUserById({ variables: { id: Number(term) } }) : Promise.resolve(),
    ]),
  );
  const toLicenseOption = (l: { id: number; companyName?: string | null; orgNumber?: string | null }): PickedOption => ({
    id: l.id,
    label: `${l.companyName || 'Uten navn'} (${l.id})${l.orgNumber ? ` · org.nr ${l.orgNumber}` : ''}`,
  });
  const toUserOption = (u: { id: number; displayName?: string | null; email?: string | null }): PickedOption => ({
    id: u.id,
    label: `${u.displayName || u.email || 'Uten navn'} (${u.id})`,
  });
  const dedupeById = (options: PickedOption[]) => options.filter((o, i) => options.findIndex((x) => x.id === o.id) === i);

  // The lazy queries keep their last data, so only surface a by-id hit while the CURRENT term
  // still is that id — otherwise a stale hit would sit on top after switching to a name search.
  const licenseByIdHit = licenseByIdSearch.data?.license?.bksLicense;
  const showLicenseByIdHit = licenseByIdHit && isNumeric(licenseTerm.trim()) && Number(licenseTerm.trim()) === licenseByIdHit.id;
  const licenseOptions: PickedOption[] = dedupeById([
    ...(showLicenseByIdHit ? [toLicenseOption(licenseByIdHit)] : []),
    ...(licenseSearch.data?.licenses?.licenses ?? []).map(toLicenseOption),
  ]);
  const userByIdHit = userByIdSearch.data?.user;
  const showUserByIdHit = userByIdHit && isNumeric(userTerm.trim()) && Number(userTerm.trim()) === userByIdHit.id;
  const userOptions: PickedOption[] = dedupeById([
    ...(showUserByIdHit ? [toUserOption(userByIdHit)] : []),
    ...(userSearch.data?.users?.users ?? []).map(toUserOption),
  ]);

  const [createRule, createState] = useMutation<any, any>(CREATE_USAGE_LIMIT, { refetchQueries: [{ query: GET_USAGE_LIMITS }] });
  const [updateRule, updateState] = useMutation<any, any>(UPDATE_USAGE_LIMIT, { refetchQueries: [{ query: GET_USAGE_LIMITS }] });
  const mutationError = createState.error ?? updateState.error;
  const saving = createState.loading || updateState.loading;

  const asIntOrNull = (value: string) => (value.trim() === '' ? null : Number(value));

  const changeProduct = (nextSourceId: number) => {
    setSourceId(nextSourceId);
    setSubscriptionIds([]); // the products have disjoint subscriptions
  };

  const validate = (): string | null => {
    if (!isEdit) {
      if (scope === 'subscription' && subscriptionIds.length === 0) return 'Velg minst ett abonnement.';
      if (scope === 'license' && !pickedLicense) return 'Velg en lisens.';
      if (scope === 'user' && !presetUserId && !pickedUser) return 'Velg en bruker.';
    }
    if ((maxDocuments.trim() === '') !== (documentWindowDays.trim() === '')) return 'Maks anvisninger og antall dager må settes sammen.';
    if ((maxAiTokens.trim() === '') !== (aiTokenWindowDays.trim() === '')) return 'Maks KI-tokens og antall dager må settes sammen.';
    return null;
  };

  const save = async () => {
    const error = validate();
    setValidationError(error);
    if (error) return;

    const limits = {
      maxDocuments: asIntOrNull(maxDocuments),
      documentWindowDays: asIntOrNull(documentWindowDays),
      maxAiTokens: asIntOrNull(maxAiTokens),
      aiTokenWindowDays: asIntOrNull(aiTokenWindowDays),
    };

    if (isEdit) {
      await updateRule({ variables: { input: { id: rule!.id, ...limits, active } } });
    } else {
      await createRule({
        variables: {
          input: {
            sourceId,
            subscriptionIds: scope === 'subscription' ? subscriptionIds : [],
            licenseId: scope === 'license' ? pickedLicense!.id : null,
            userId: scope === 'user' ? (presetUserId ?? pickedUser!.id) : null,
            ...limits,
          },
        },
      });
    }
    onSaved?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Endre bruksgrense' : 'Ny bruksgrense'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!isEdit && !presetSourceId && (
            <RadioGroup row value={sourceId} onChange={(e) => changeProduct(Number(e.target.value))}>
              <FormControlLabel value={BYGGFORSKSERIEN} control={<Radio />} label="Byggforskserien" />
              <FormControlLabel value={VAATROMSNORMEN} control={<Radio />} label="Våtromsnormen" />
            </RadioGroup>
          )}
          {!isEdit && presetSourceId && (
            <Typography variant="body2" color="textSecondary">
              Produkt: {productName(presetSourceId)}
            </Typography>
          )}

          {!isEdit && !presetUserId && (
            <>
              {!presetSubscriptionId && (
                <RadioGroup row value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
                  <FormControlLabel value="subscription" control={<Radio />} label="Abonnement" />
                  <FormControlLabel value="license" control={<Radio />} label="Lisens" />
                  <FormControlLabel value="user" control={<Radio />} label="Bruker" />
                  <FormControlLabel value="global" control={<Radio />} label="Global (alle brukere)" />
                </RadioGroup>
              )}

              {scope === 'global' && (
                <Typography variant="body2" color="textSecondary">
                  Gjelder alle brukere av produktet, og håndheves alltid i tillegg til brukerens egen regel. Maks én aktiv global grense per produkt.
                </Typography>
              )}

              {scope === 'subscription' && (
                <FormControl fullWidth>
                  <InputLabel>Abonnementer</InputLabel>
                  <Select
                    multiple
                    label="Abonnementer"
                    value={subscriptionIds}
                    onChange={(e) => setSubscriptionIds(e.target.value as number[])}
                    renderValue={(selected) => selected.map((id) => subscriptions.find((s) => s.id === id)?.name ?? id).join(', ')}
                  >
                    {subscriptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Checkbox checked={subscriptionIds.includes(s.id)} />
                        <ListItemText primary={s.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {scope === 'license' && (
                <Autocomplete
                  options={licenseOptions}
                  value={pickedLicense}
                  onChange={(_, value) => setPickedLicense(value)}
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'input') return;
                    setLicenseTerm(value);
                    licenseSearchControl.onInput(value);
                  }}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  loading={licenseSearchControl.pending || licenseSearch.loading}
                  loadingText="Søker …"
                  filterOptions={(x) => x}
                  noOptionsText="Søk på firmanavn, org.nr eller kundenummer"
                  renderInput={(params) => <TextField {...params} label="Lisens" placeholder="Søk etter lisens …" />}
                />
              )}
              {scope === 'user' && (
                <Autocomplete
                  options={userOptions}
                  value={pickedUser}
                  onChange={(_, value) => setPickedUser(value)}
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'input') return;
                    setUserTerm(value);
                    userSearchControl.onInput(value);
                  }}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  loading={userSearchControl.pending || userSearch.loading}
                  loadingText="Søker …"
                  filterOptions={(x) => x}
                  noOptionsText="Søk på navn eller e-post"
                  renderInput={(params) => <TextField {...params} label="Bruker" placeholder="Søk etter bruker …" />}
                />
              )}
            </>
          )}

          {isEdit && (
            <Typography variant="body2" color="textSecondary">
              Produkt: {productName(rule!.sourceId)}. Gjelder: {scopeLabel(rule!)} (kan ikke endres)
            </Typography>
          )}

          <Typography variant="subtitle2">Anvisninger</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Maks antall" type="number" value={maxDocuments} onChange={(e) => setMaxDocuments(e.target.value)} fullWidth />
            <TextField
              label="Innenfor antall dager"
              type="number"
              value={documentWindowDays}
              onChange={(e) => setDocumentWindowDays(e.target.value)}
              fullWidth
            />
          </Box>

          <Typography variant="subtitle2">KI-tokens</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Maks antall" type="number" value={maxAiTokens} onChange={(e) => setMaxAiTokens(e.target.value)} fullWidth />
            <TextField
              label="Innenfor antall dager"
              type="number"
              value={aiTokenWindowDays}
              onChange={(e) => setAiTokenWindowDays(e.target.value)}
              fullWidth
            />
          </Box>

          <Typography variant="caption" color="textSecondary">
            Tomme felter betyr ingen grense.
          </Typography>

          {isEdit && <FormControlLabel control={<Switch checked={active} onChange={() => setActive(!active)} />} label="Aktiv" />}

          {validationError && <Alert severity="warning">{validationError}</Alert>}
          {mutationError && <Alert severity="error">Feil! {mutationError.message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" variant="contained" onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" color="primary" onClick={save} disabled={saving}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function scopeLabel(
  rule: {
    subscriptionIds?: number[] | null;
    licenseId?: number | null;
    licenseName?: string | null;
    userId?: number | null;
    userName?: string | null;
  },
  subscriptionNames?: Map<number, string>,
) {
  if (rule.userId) return rule.userName ?? `Bruker ${rule.userId}`;
  if (rule.licenseId) return rule.licenseName ?? `Lisens ${rule.licenseId}`;
  const ids = rule.subscriptionIds ?? [];
  if (ids.length === 0) return 'Global (alle brukere)';
  return ids.map((id) => subscriptionNames?.get(id) ?? `Abonnement ${id}`).join(', ');
}
